import { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { generateCoachingMessage, getGeminiApiKey } from '../services/geminiService';
import { getKoreanToday } from '../utils/dateUtils';
import { calculateBookProgress } from '../utils/bookUtils';
import { recordTimeMs } from '../utils/recordUtils';

const CACHE_KEY = 'coaching_cache_v3';
const DISMISSED_HASHES_KEY = 'coaching_dismissed_hashes';
const LAST_GENERATED_KEY = 'coaching_last_generated';

// 환경변수에서 설정 읽기 (기본값 제공)
const COOLDOWN_HOURS = parseFloat(import.meta.env.VITE_COACHING_COOLDOWN_HOURS || '1');
const MAX_DAILY_GENERATIONS = parseInt(import.meta.env.VITE_COACHING_MAX_DAILY || '20', 10);
// 디바운스. 기록은 연달아 여러 개를 쓰는 일이 흔해서 더 길게 잡는다
// (타이머가 매번 초기화되므로 '쓰기를 멈춘 뒤' 한 번만 호출된다)
const DEBOUNCE_MS = 1500;
const DEBOUNCE_MS_RECORD = 8000;

// AI 에 넘길 최근 기록 개수와 발췌 길이
const RECENT_RECORDS_FOR_AI = 6;
const RECORD_EXCERPT_LEN = 160;

/** 오늘 남긴 기록 수 */
const countTodayRecords = (records, today) =>
  records.filter(r => r.date === today).length;

// 상태를 버킷화하여 해시 생성
const createStateHash = (books, streakData, completedBooks, records = []) => {
  const today = getKoreanToday();

  // 진도율 버킷화 (5단계)
  const progressBucket = (progress) => {
    if (progress < 5) return 'start';
    if (progress < 25) return 'early';
    if (progress < 50) return 'mid';
    if (progress < 75) return 'late';
    if (progress < 95) return 'almost';
    return 'done';
  };

  // 스트릭 버킷화
  const streakBucket = (streak) => {
    if (streak === 0) return '0';
    if (streak <= 2) return '1-2';
    if (streak <= 6) return '3-6';
    if (streak <= 13) return '7-13';
    if (streak <= 29) return '14-29';
    return '30+';
  };

  // 마지막 독서일로부터 경과일 버킷화
  const daysSinceBucket = (lastReadDate) => {
    if (!lastReadDate) return 'never';
    const days = Math.floor((new Date(today) - new Date(lastReadDate)) / (1000 * 60 * 60 * 24));
    if (days === 0) return 'today';
    if (days === 1) return 'yesterday';
    if (days <= 3) return '2-3days';
    if (days <= 7) return 'week';
    return 'long';
  };

  // 시간대 버킷
  const hour = new Date().getHours();
  const timeBucket = hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening';

  // 활성 책 상태 요약 (최대 3권) - 공통 유틸 함수 사용
  const bookStates = books.slice(0, 3).map(book => {
    const progress = calculateBookProgress(book);
    const todayRead = (book.readingHistory || {})[today] || 0;

    return {
      id: book.id,
      progressBucket: progressBucket(progress),
      todayReadBucket: todayRead === 0 ? '0' : todayRead < 10 ? '1-9' : todayRead < 30 ? '10-29' : '30+'
    };
  }).sort((a, b) => a.id.localeCompare(b.id));

  // 오늘 완독한 책 있는지
  const todayCompleted = completedBooks.some(b => b.completedDate === today);

  const stateObj = {
    date: today,
    timeBucket,
    streakBucket: streakBucket(streakData?.currentStreak || 0),
    daysSinceBucket: daysSinceBucket(streakData?.lastReadDate),
    bookStates,
    todayCompleted,
    bookCount: books.length,
    // 코치가 다루는 책이 바뀌면 메시지도 새로 받아야 한다
    focusBookId: selectFocusBook(books, completedBooks, today, records)?.book?.id || null,
    // 기록을 남기면 새 메시지를 받을 수 있어야 하므로 해시에 포함한다
    todayRecordCount: countTodayRecords(records, today)
  };

  return JSON.stringify(stateObj);
};

/**
 * 코치가 말할 책 1권을 고른다.
 *
 * 코치는 이 한 권에 대해서만 말한다 — 서로 다른 책을 억지로 잇지 않기 위해서다.
 * 그래서 '오늘 기록을 남긴 책'을 앞순위에 둔다. 코치가 반응할 재료(인용·생각)가
 * 있는 책이라야 할 말이 생기고, 그 재료와 통계가 같은 책의 것이 된다.
 */
const selectFocusBook = (books, completedBooks, today, records = []) => {
  // 1순위: 오늘 완독한 책
  const todayCompleted = completedBooks.find(b => b.completedDate === today);
  if (todayCompleted) {
    return {
      book: todayCompleted,
      reason: 'completed',
      progress: 100,
      todayRead: (todayCompleted.readingHistory || {})[today] || 0
    };
  }

  // 책 정보 계산 - 공통 유틸 함수 사용
  const booksWithProgress = books.map(book => {
    const progress = calculateBookProgress(book);
    const todayRead = (book.readingHistory || {})[today] || 0;
    return { book, progress, todayRead };
  });

  // 2순위: 오늘 기록을 남긴 책 (여럿이면 가장 많이 남긴 책)
  const todayRecordCounts = new Map();
  records.forEach(r => {
    if (r.date !== today) return;
    todayRecordCounts.set(r.bookId, (todayRecordCounts.get(r.bookId) || 0) + 1);
  });
  if (todayRecordCounts.size > 0) {
    const withRecords = booksWithProgress
      .filter(b => todayRecordCounts.has(b.book.id))
      .sort((a, b) => todayRecordCounts.get(b.book.id) - todayRecordCounts.get(a.book.id));
    if (withRecords[0]) {
      return { ...withRecords[0], reason: 'recorded_today' };
    }
  }

  // 3순위: 85% 이상인 책 (완독 임박)
  const almostDone = booksWithProgress.find(b => b.progress >= 85 && b.progress < 100);
  if (almostDone) {
    return { ...almostDone, reason: 'almost_done' };
  }

  // 4순위: 오늘 가장 많이 읽은 책
  const sortedByToday = [...booksWithProgress].sort((a, b) => b.todayRead - a.todayRead);
  if (sortedByToday[0]?.todayRead > 0) {
    return { ...sortedByToday[0], reason: 'most_read_today' };
  }

  // 5순위: 진도율이 가장 높은 책
  const sortedByProgress = [...booksWithProgress].sort((a, b) => b.progress - a.progress);
  if (sortedByProgress[0]) {
    return { ...sortedByProgress[0], reason: 'highest_progress' };
  }

  return null;
};

// 날짜 문자열 생성 (today 기준 daysAgo일 전, 타임존 무관)
const getDateStr = (todayStr, daysAgo) => {
  const [y, m, d] = todayStr.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d - daysAgo)).toISOString().split('T')[0];
};

/**
 * 최근 기록을 AI 가 읽을 수 있는 형태로 추린다.
 * 코치가 '얼마나 읽었는지'만이 아니라 '무엇을 생각했는지'에 반응하려면 이 재료가 필요하다.
 *
 * 받는 records 는 이미 집중 도서 한 권의 것으로 걸러진 상태다 — 다른 책의 문장이
 * 섞여 들어가면 코치가 두 책을 엮어 말하게 된다.
 */
const summarizeRecords = (bookRecords, today, weekAgo) => {
  if (!bookRecords || bookRecords.length === 0) {
    return { todayCount: 0, weekCount: 0, quoteCount: 0, thoughtCount: 0, recent: [] };
  }

  const inWeek = bookRecords.filter(r => r.date >= weekAgo);

  const recent = [...bookRecords]
    .sort((a, b) => recordTimeMs(b) - recordTimeMs(a))
    .slice(0, RECENT_RECORDS_FOR_AI)
    .map(r => ({
      type: r.type === 'quote' ? '인용' : '생각',
      page: r.page || null,
      date: r.date,
      text: r.text.length > RECORD_EXCERPT_LEN
        ? `${r.text.slice(0, RECORD_EXCERPT_LEN)}…`
        : r.text
    }));

  return {
    todayCount: bookRecords.filter(r => r.date === today).length,
    weekCount: inWeek.length,
    quoteCount: inWeek.filter(r => r.type === 'quote').length,
    thoughtCount: inWeek.filter(r => r.type === 'thought').length,
    recent
  };
};

// 요약 컨텍스트 생성 (LLM에 전달할 데이터) - 주간 분석 포함
const createStateSummary = (books, streakData, completedBooks, eventType = null, records = []) => {
  const today = getKoreanToday();
  const hour = new Date().getHours();
  const timePeriod = hour < 12 ? '아침' : hour < 18 ? '오후' : '저녁';

  // 집중할 책 1권 선택. 이 아래의 모든 재료는 이 한 권의 것만 담는다
  const focusBook = selectFocusBook(books, completedBooks, today, records);
  const focusId = focusBook?.book?.id;
  const focusHistory = focusBook?.book?.readingHistory || {};

  // 주간 통계 — 집중 도서 한 권 기준 (최근 7일 vs 이전 7일)
  let thisWeekTotal = 0;
  let lastWeekTotal = 0;
  let thisWeekActiveDays = 0;

  for (let i = 0; i < 7; i++) {
    const dayTotal = focusHistory[getDateStr(today, i)] || 0;
    thisWeekTotal += dayTotal;
    if (dayTotal > 0) thisWeekActiveDays++;
  }

  for (let i = 7; i < 14; i++) {
    lastWeekTotal += focusHistory[getDateStr(today, i)] || 0;
  }

  const weeklyTrend = lastWeekTotal > 0
    ? Math.round(((thisWeekTotal - lastWeekTotal) / lastWeekTotal) * 100)
    : null;
  const avgDailyPages = thisWeekActiveDays > 0
    ? Math.round(thisWeekTotal / thisWeekActiveDays)
    : 0;

  // 집중 도서 완독 예측 (공통 유틸 함수로 정확한 읽은 페이지 계산)
  let daysToComplete = null;
  let focusRemainingPages = null;
  if (focusBook && focusBook.book.totalPages) {
    const totalRead = calculateBookProgress(focusBook.book) / 100 * focusBook.book.totalPages;
    focusRemainingPages = Math.max(0, Math.round(focusBook.book.totalPages - totalRead));
    if (avgDailyPages > 0 && focusRemainingPages > 0) {
      daysToComplete = Math.ceil(focusRemainingPages / avgDailyPages);
    }
  }

  return {
    timePeriod,
    // 스트릭은 습관 지표라 책과 무관하게 그대로 둔다
    streak: streakData?.currentStreak || 0,
    totalReadingDays: streakData?.totalReadingDays || 0,
    focusBook: focusBook ? {
      title: focusBook.book.title,
      progress: focusBook.progress,
      todayRead: focusBook.todayRead,
      reason: focusBook.reason,
      remainingPages: focusRemainingPages,
      daysToComplete
    } : null,
    weeklyStats: {
      thisWeekTotal,
      thisWeekPeriod: `${getDateStr(today, 6)} ~ ${today}`,
      lastWeekTotal,
      lastWeekPeriod: `${getDateStr(today, 13)} ~ ${getDateStr(today, 7)}`,
      weeklyTrend,
      avgDailyPages,
      activeDays: thisWeekActiveDays
    },
    records: summarizeRecords(
      focusId ? records.filter(r => r.bookId === focusId) : [],
      today,
      getDateStr(today, 6)
    ),
    eventType
  };
};

// stateSummary → 한국어 팩트시트 변환 (AI가 수치를 그대로 인용하도록)
const formatStatsSummaryText = (summary) => {
  const lines = [];

  const fb = summary.focusBook;
  const title = fb ? `'${fb.title}'` : '이 책';

  lines.push('**사실 데이터 (이 수치만 정확히 사용하세요)**:');
  if (fb) {
    lines.push(`- 지금 이야기할 책: ${title} — 이 책 하나에 대해서만 말하세요`);
  }
  lines.push(`- 현재 시간대: ${summary.timePeriod}`);

  if (fb) {
    let bookLine = `- ${title} 진도: ${fb.progress}%`;
    if (fb.remainingPages !== null) bookLine += `, 남은 ${fb.remainingPages}페이지`;
    if (fb.daysToComplete !== null) bookLine += `, 이 속도면 약 ${fb.daysToComplete}일 소요 예상`;
    lines.push(bookLine);
    lines.push(`- 오늘 ${title} 읽은 양: ${fb.todayRead}페이지`);
    if (fb.reason === 'completed') {
      lines.push('- 오늘 완독!');
    }
  }

  // 아래 쪽수는 전부 집중 도서 한 권의 것이다 (다른 책은 합산하지 않는다)
  const ws = summary.weeklyStats;
  if (fb) {
    lines.push(`- ${title} 최근 7일(${ws.thisWeekPeriod}): 총 ${ws.thisWeekTotal}페이지, ${ws.activeDays}일 독서, 읽은 날 기준 일평균 ${ws.avgDailyPages}페이지`);
    lines.push(`- ${title} 이전 7일(${ws.lastWeekPeriod}): 총 ${ws.lastWeekTotal}페이지`);

    if (ws.weeklyTrend !== null) {
      const sign = ws.weeklyTrend >= 0 ? '+' : '';
      lines.push(`- ${title} 증감: ${sign}${ws.weeklyTrend}%`);
    }
  }

  lines.push(`- 연속 독서: ${summary.streak}일째 (책 전체 기준)`);
  lines.push(`- 누적 독서일: ${summary.totalReadingDays}일 (책 전체 기준)`);

  const rec = summary.records;
  if (rec) {
    lines.push(`- ${title}에 남긴 기록: 오늘 ${rec.todayCount}개, 최근 7일 ${rec.weekCount}개 (인용 ${rec.quoteCount} · 생각 ${rec.thoughtCount})`);
  }

  if (summary.eventType) {
    const eventLabels = {
      progress_update: '독서 기록 직후',
      completed: '완독 직후',
      record_added: '인용/생각을 남긴 직후',
      manual: '사용자가 직접 새 메시지를 요청함'
    };
    lines.push(`- 상황: ${eventLabels[summary.eventType] || summary.eventType}`);
  }

  // 기록 원문은 팩트 목록과 분리해서 붙인다 (수치가 아니라 '재료'이므로)
  if (rec?.recent?.length > 0) {
    lines.push('');
    lines.push(`**사용자가 ${title}에 최근 남긴 기록** (전부 이 책의 것입니다. 이 내용에 반응해주세요):`);
    rec.recent.forEach(r => {
      const where = r.page ? ` p.${r.page}` : '';
      lines.push(`- [${r.type}]${where} (${r.date}): ${r.text}`);
    });
  }

  return lines.join('\n');
};

// localStorage 안전하게 읽기
const safeGetItem = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.warn(`[useCoaching] localStorage read failed for ${key}:`, error.message);
    return defaultValue;
  }
};

// localStorage 안전하게 쓰기
const safeSetItem = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (error) {
    console.warn(`[useCoaching] localStorage write failed for ${key}:`, error.message);
    return false;
  }
};

export const useCoaching = (books, streakData, completedBooks = [], records = []) => {
  const [coaching, setCoaching] = useState({
    message: null,
    loading: false,
    dismissed: false
  });

  const hasRunRef = useRef(false);
  const booksRef = useRef(books);
  const streakRef = useRef(streakData);
  const completedRef = useRef(completedBooks);
  const recordsRef = useRef(records);
  const debounceTimerRef = useRef(null);

  // refs 업데이트
  useEffect(() => {
    booksRef.current = books;
    streakRef.current = streakData;
    completedRef.current = completedBooks;
    recordsRef.current = records;
  }, [books, streakData, completedBooks, records]);

  const dismissCoaching = useCallback(() => {
    const stateHash = createStateHash(books, streakData, completedBooks, records);
    const existing = safeGetItem(DISMISSED_HASHES_KEY, []);
    if (!existing.includes(stateHash)) {
      existing.push(stateHash);
      // 최근 10개만 유지
      if (existing.length > 10) existing.shift();
      safeSetItem(DISMISSED_HASHES_KEY, existing);
    }
    setCoaching(prev => ({ ...prev, dismissed: true }));
  }, [books, streakData, completedBooks, records]);

  // 즉각 피드백 트리거 함수 (기록 직후 호출) - 디바운스 적용
  const triggerFeedback = useCallback(async (eventType = 'progress_update') => {
    // 기존 타이머 취소
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // 디바운스 적용
    debounceTimerRef.current = setTimeout(async () => {
      const currentBooks = booksRef.current;
      const currentStreak = streakRef.current;
      const currentCompleted = completedRef.current;
      const currentRecords = recordsRef.current;

      // 아래 중단 경로들은 refreshCoaching 이 미리 켜둔 로딩을 반드시 꺼야 한다
      const abort = (notice) => {
        if (eventType === 'manual' && notice) notice();
        setCoaching(prev => (prev.loading ? { ...prev, loading: false } : prev));
      };

      if (!currentBooks || currentBooks.length === 0) return abort();
      if (!getGeminiApiKey()) {
        return abort(() => toast.error('설정에서 Gemini API 키를 먼저 등록해주세요'));
      }

      const today = getKoreanToday();

      // 일일 생성 횟수 체크만 (쿨다운 무시)
      const lastGenerated = safeGetItem(LAST_GENERATED_KEY, {});
      if (lastGenerated.date === today && lastGenerated.count >= MAX_DAILY_GENERATIONS) {
        console.log('[useCoaching] Daily generation limit reached');
        // 사용자가 직접 누른 경우엔 아무 일도 안 일어난 것처럼 보이면 안 된다
        return abort(() => toast(`오늘 코치 메시지 한도(${MAX_DAILY_GENERATIONS}회)를 다 썼어요`));
      }

      setCoaching(prev => ({ ...prev, loading: true, dismissed: false }));

      const stateSummary = createStateSummary(
        currentBooks, currentStreak, currentCompleted, eventType, currentRecords
      );
      const formattedText = formatStatsSummaryText(stateSummary);

      // 이전 메시지 가져오기
      const cache = safeGetItem(CACHE_KEY, {});
      const previousMessage = cache.message || null;

      const message = await generateCoachingMessage(formattedText, previousMessage);

      if (message) {
        const stateHash = createStateHash(currentBooks, currentStreak, currentCompleted, currentRecords);

        safeSetItem(CACHE_KEY, {
          hash: stateHash,
          message,
          date: today
        });

        const newCount = lastGenerated.date === today ? (lastGenerated.count || 0) + 1 : 1;
        safeSetItem(LAST_GENERATED_KEY, {
          timestamp: Date.now(),
          date: today,
          count: newCount
        });

        setCoaching({ message, loading: false, dismissed: false });
      } else {
        if (eventType === 'manual') {
          toast.error('메시지를 받지 못했어요. 잠시 후 다시 시도해주세요');
        }
        setCoaching(prev => ({ ...prev, loading: false }));
      }
    }, eventType === 'record_added' ? DEBOUNCE_MS_RECORD : DEBOUNCE_MS);
  }, []);

  /**
   * 사용자가 버튼으로 직접 요청 — 쿨다운과 캐시를 건너뛴다.
   * (일일 한도는 API 비용 폭주를 막기 위해 유지)
   */
  const refreshCoaching = useCallback(() => {
    // 닫은 상태를 초기화해야 새 메시지가 보인다.
    // 로딩은 즉시 켠다 — 버튼을 눌렀는데 디바운스만큼 아무 반응이 없으면 안 되므로.
    safeSetItem(DISMISSED_HASHES_KEY, []);
    setCoaching(prev => ({ ...prev, dismissed: false, loading: true }));
    triggerFeedback('manual');
  }, [triggerFeedback]);

  // 컴포넌트 언마운트 시 타이머 정리
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // 초기 로드 시 메시지 체크
  useEffect(() => {
    if (hasRunRef.current) return;
    if (!books || books.length === 0) return;

    const today = getKoreanToday();
    const stateHash = createStateHash(books, streakData, completedBooks, records);

    const fetchCoaching = async () => {
      hasRunRef.current = true;

      // 1. 이미 닫은 상태인지 확인
      const dismissedHashes = safeGetItem(DISMISSED_HASHES_KEY, []);
      if (dismissedHashes.includes(stateHash)) {
        setCoaching(prev => ({ ...prev, dismissed: true }));
        return;
      }

      // 2. 캐시된 메시지 확인 (동일 해시)
      const cache = safeGetItem(CACHE_KEY, {});
      if (cache.hash === stateHash && cache.message) {
        setCoaching({ message: cache.message, loading: false, dismissed: false });
        return;
      }

      // 3. 쿨다운 체크 (마지막 생성 후 N시간)
      const lastGenerated = safeGetItem(LAST_GENERATED_KEY, null);
      if (lastGenerated) {
        const hoursSince = (Date.now() - lastGenerated.timestamp) / (1000 * 60 * 60);

        // 쿨다운 미경과
        if (hoursSince < COOLDOWN_HOURS) {
          return;
        }

        // 오늘 생성 횟수 초과
        if (lastGenerated.date === today && lastGenerated.count >= MAX_DAILY_GENERATIONS) {
          return;
        }
      }

      // 4. 오늘 활동이 있었는지.
      // 쪽수뿐 아니라 인용/생각을 남긴 것도 활동으로 친다 —
      // 진도를 올리지 않고 문장만 모으는 날이 오히려 코치가 할 말이 많은 날이다.
      const todayHasPages = books.some(book => (book.readingHistory || {})[today] > 0);
      const todayHasRecords = countTodayRecords(records, today) > 0;
      const todayCompleted = completedBooks.some(b => b.completedDate === today);

      if (!todayHasPages && !todayHasRecords && !todayCompleted) {
        return; // 오늘 아무것도 안 했으면 메시지 생성 안 함
      }

      // 5. API 키 확인
      if (!getGeminiApiKey()) {
        return;
      }

      // 6. 메시지 생성
      setCoaching(prev => ({ ...prev, loading: true }));

      const stateSummary = createStateSummary(books, streakData, completedBooks, null, records);
      const formattedText = formatStatsSummaryText(stateSummary);

      // 이전 메시지 가져오기 (반복 방지용)
      const previousMessage = cache.message || null;

      const message = await generateCoachingMessage(formattedText, previousMessage);

      if (message) {
        // 캐시 저장
        safeSetItem(CACHE_KEY, {
          hash: stateHash,
          message,
          date: today
        });

        // 생성 기록 업데이트
        const prevGenerated = safeGetItem(LAST_GENERATED_KEY, {});
        const newCount = prevGenerated.date === today ? (prevGenerated.count || 0) + 1 : 1;
        safeSetItem(LAST_GENERATED_KEY, {
          timestamp: Date.now(),
          date: today,
          count: newCount
        });

        setCoaching({ message, loading: false, dismissed: false });
      } else {
        setCoaching(prev => ({ ...prev, loading: false }));
      }
    };

    fetchCoaching();
  }, [books, completedBooks, streakData, records]);

  return {
    ...coaching,
    dismissCoaching,
    triggerFeedback,
    refreshCoaching
  };
};
